import React, { useCallback, useState } from 'react';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import { useDropzone } from 'react-dropzone';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
} from 'reactstrap';
import loading from '../../assets/gif/loading.gif';

interface ViewEntry {
  view: string;
  file: File;
  preview: string;
}

export type Props = {
  description: string;
  handleUpload: (entries: Array<{ view: string; file: File }>) => void;
};

const AdminUploadViewResource: React.FC<Props> = ({ description, handleUpload }) => {
  const [viewName, setViewName] = useState('');
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [currentPreview, setCurrentPreview] = useState<string>('');
  const [entries, setEntries] = useState<ViewEntry[]>([]);
  const [viewNameError, setViewNameError] = useState('');
  const [fileError, setFileError] = useState('');
  const { isLoading } = useCallApiAndLoad();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setCurrentFile(file);
      setCurrentPreview(URL.createObjectURL(file));
      setFileError('');
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: 1,
    onDrop,
  });

  const handleAdd = useCallback(() => {
    let hasError = false;

    if (viewName.trim()) {
      setViewNameError('');
    } else {
      setViewNameError('El nombre de vista es requerido');
      hasError = true;
    }

    if (currentFile) {
      setFileError('');
    } else {
      setFileError('Debes seleccionar una imagen');
      hasError = true;
    }

    if (hasError) return;

    setEntries((prev) => [
      ...prev,
      { view: viewName.trim(), file: currentFile!, preview: currentPreview },
    ]);
    setViewName('');
    setCurrentFile(null);
    setCurrentPreview('');
  }, [viewName, currentFile, currentPreview]);

  const removeEntry = useCallback((index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return (
    <Card>
      <CardHeader>
        <h4>{description}</h4>
      </CardHeader>
      <CardBody>
        <ListGroup className="list my--3" flush>
          <ListGroupItem className="px-0">
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>Nombre de vista *</Label>
                  <Input
                    type="text"
                    placeholder="ej: sala, cocina, baño..."
                    value={viewName}
                    onChange={(e) => {
                      setViewName(e.target.value);
                      if (e.target.value.trim()) setViewNameError('');
                    }}
                    invalid={!!viewNameError}
                  />
                  {viewNameError && (
                    <div className="invalid-feedback">{viewNameError}</div>
                  )}
                </FormGroup>
                <div {...getRootProps({ className: 'px-4 py-4 text-center dropzone' })}>
                  <input {...getInputProps()} />
                  {currentPreview ? (
                    <img
                      src={currentPreview}
                      alt="preview"
                      className="rounded"
                      width={175}
                      height={100}
                      style={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <>
                      <p>Arrastra tu foto o Selecciona</p>
                      <span className="text-sm">
                        Formatos soportados: JPEG y PNG, máximo de 1MB por archivo.
                      </span>
                    </>
                  )}
                </div>
                {fileError && (
                  <div className="text-danger text-sm mt-1">{fileError}</div>
                )}
                <div className="text-right mt-2">
                  <Button color="primary" size="sm" type="button" onClick={handleAdd}>
                    + Agregar
                  </Button>
                </div>
              </Col>
            </Row>
          </ListGroupItem>
        </ListGroup>

        {entries.map((entry, idx) => (
          <ListGroup className="list my--3" flush key={`${entry.view}-${idx}`}>
            <ListGroupItem className="px-0">
              <Row className="align-items-center">
                <Col className="col-auto">
                  <img
                    className="rounded"
                    alt="..."
                    width={175}
                    height={100}
                    src={entry.preview}
                    style={{ objectFit: 'cover' }}
                    onLoad={() => {}}
                  />
                </Col>
                <Col>
                  <strong>{entry.view}</strong>
                </Col>
                <Col className="col-auto">
                  <Button
                    onClick={() => removeEntry(idx)}
                    color="danger"
                    size="sm"
                    type="button"
                  >
                    Eliminar
                  </Button>
                </Col>
              </Row>
            </ListGroupItem>
          </ListGroup>
        ))}
      </CardBody>
      <CardFooter>
        <Row>
          <div className="col ml--2" />
          {isLoading && <img src={loading} alt="" width={45} />}
          <Col className="col-auto">
            <Button
              onClick={() => handleUpload(entries.map(({ view, file }) => ({ view, file })))}
              color="success"
              size="md"
              type="button"
              disabled={isLoading || entries.length === 0}
            >
              <i className="fa fa-duotone fa-upload mr-1" /> Subir
            </Button>
          </Col>
        </Row>
      </CardFooter>
    </Card>
  );
};

export default AdminUploadViewResource;
